import { clamp } from '../Math.js';
import { ViewerFramebufferType } from '../babylon/Framebuffer.js';
// Flags to change emulated behaviour of different rounding events.
// We use this to emulate behaviour found on the GPU, but the exact GPU behaviour is device-dependant and not easy to figure out.
// Thus we have to settle for something here.
const FLOAT_DOWNCAST_TRUNCATE = true;
const FLOAT_NORMALIZATION_TRUNCATE = false;
// Flag to change how we emulate F32 in Javascript. Setting this to true will truncate instead of round to F32, but it's also very slow.
const USE_MANTISSA_TRUNCATION_F64_F32_EMULATION = false;
const R11_G11_B10F_COMPONENT_MAX_ERROR = 3;
// INTERNALS
// DO NOT CHANGE, change the FLOAT_DOWNCAST_TRUNCATE flag instead.
const f11_f10_round_bit_mask = FLOAT_DOWNCAST_TRUNCATE ? 0b0 : 0b1;
// ONLY CHANGE IF WE CHANGE IN THE RENDERING PIPELINE
const GAMMA = 2.2;
// Helper function for this file to emulate f32 from out f64's in javascript.
// Can use a more precise but *much* slower method, or a faster method.
// The precise method should only be needed for verification in development.
function double_truncater(x) {
    if (USE_MANTISSA_TRUNCATION_F64_F32_EMULATION) {
        return truncate_mantissa_f64_f32(x);
    }
    else {
        return Math.fround(x);
    }
}
// Converts a double to 2 unsigned integers that have the bit pattern of the double, split into two parts.
function double_to_ieee(f) {
    const buffer = new ArrayBuffer(8);
    const f64_buffer = new Float64Array(buffer);
    const u32_buffer = new Uint32Array(buffer);
    f64_buffer[0] = f;
    return u32_buffer;
}
// The reverse of the above function. Takes 2 unsigned integers that together form the bit pattern of a double, and returns the double.
function ieee_to_double(f) {
    const buffer = new ArrayBuffer(8);
    (new Uint32Array(buffer))[0] = f[0];
    (new Uint32Array(buffer))[1] = f[1];
    return new Float64Array(buffer)[0];
}
// Truncates the mantissa of the given f64 to be of f32 precision
// Does not affect exponent
function truncate_mantissa_f64_f32(x) {
    const ieee_f64 = double_to_ieee(x);
    ieee_f64[0] = (ieee_f64[0] >> 29) << 29;
    return ieee_to_double(ieee_f64);
}
// Converts an IEE-754 double (a javascript 'number') to the OpenGL's specification of a 10-bit unsigned floating point number, and then back to a double.
// if add_step is defined, truncating will be used when converting, and add_step number of bits will be added to the float in its compressed format
function convert_f64_f10(x, add_step) {
    if (x <= 0 || x >= 65024)
        return clamp(x, 0, 65024);
    x = Math.max(0.0, x);
    const ieee_f64 = double_to_ieee(x);
    const f64_bias = 1023;
    const f11_bias = 15;
    const exponent_f64 = ((ieee_f64[1] >> 20) & 0b11111111111) - f64_bias;
    if (exponent_f64 === -f64_bias || exponent_f64 + f11_bias < 0) {
        // Subnormal number, or otherwise will be truncated to zero in F11
        return 0.0;
    }
    let exponent_f10 = ((clamp(exponent_f64 + f11_bias, 0, 30)) & 0b11111) - f11_bias;
    let mantissa_f10 = (ieee_f64[1] >> 15) & 0b11111;
    const round_bit = add_step === undefined ? ((ieee_f64[1] >> 14) & f11_f10_round_bit_mask) : add_step;
    const combined = ((exponent_f10 << 5) | mantissa_f10) + round_bit;
    exponent_f10 = combined >> 5;
    mantissa_f10 = combined & 0b11111;
    const packed = new Uint32Array(2);
    packed[0] = 0;
    packed[1] = ((exponent_f10 + f64_bias) << 20) | (mantissa_f10 << 15);
    const f10_as_f64 = ieee_to_double(packed);
    return f10_as_f64;
}
// Converts an IEE-754 double (a javascript 'number') to the OpenGL's specification of a 11-bit unsigned floating point number, and then back to a double.
// if add_step is defined, truncating will be used when converting, and add_step number of bits will be added to the float in its compressed format
function convert_f64_f11(x, add_step) {
    if (x <= 0 || x >= 65024)
        return clamp(x, 0, 65024);
    x = Math.max(0.0, x);
    const ieee_f64 = double_to_ieee(x);
    const f64_bias = 1023;
    const f11_bias = 15;
    const exponent_f64 = ((ieee_f64[1] >> 20) & 0b11111111111) - f64_bias;
    if (exponent_f64 === -f64_bias || exponent_f64 + f11_bias < 0) {
        // Subnormal number, or otherwise will be truncated to zero in F11
        return 0.0;
    }
    let exponent_f11 = ((clamp(exponent_f64 + f11_bias, 0, 30)) & 0b11111) - f11_bias;
    let mantissa_f11 = (ieee_f64[1] >> 14) & 0b111111;
    const round_bit = add_step === undefined ? ((ieee_f64[1] >> 13) & f11_f10_round_bit_mask) : add_step;
    const combined = ((exponent_f11 << 6) | mantissa_f11) + round_bit;
    exponent_f11 = combined >> 6;
    mantissa_f11 = combined & 0b111111;
    const packed = new Uint32Array(2);
    packed[0] = 0;
    packed[1] = ((exponent_f11 + f64_bias) << 20) | (mantissa_f11 << 14);
    const f11_as_f64 = ieee_to_double(packed);
    return f11_as_f64;
}
// Color compression and utils
// Clamps the color (0-1) and converts it to sRGB.
function to_srgb_and_clamp(linear_color) {
    return saturate({
        x: Math.pow(linear_color.x, 1.0 / GAMMA),
        y: Math.pow(linear_color.y, 1.0 / GAMMA),
        z: Math.pow(linear_color.z, 1.0 / GAMMA)
    });
}
// A function to emulate storing data in a R11F_G11F_B10F format, which we can use in the Viewer.
function compress_r11g11b10f(color) {
    return {
        x: convert_f64_f11(color.x),
        y: convert_f64_f11(color.y),
        z: convert_f64_f10(color.z)
    };
}
// Clamp values to 0 - 1
function saturate(color) {
    return {
        x: Math.min(1.0, Math.max(0.0, color.x)),
        y: Math.min(1.0, Math.max(0.0, color.y)),
        z: Math.min(1.0, Math.max(0.0, color.z))
    };
}
// Compresses the input float color to integers between 0 - 255
function compress_rgba8ui(color_float) {
    if (FLOAT_NORMALIZATION_TRUNCATE) {
        return {
            x: clamp(Math.floor(color_float.x * 255.0), 0, 255),
            y: clamp(Math.floor(color_float.y * 255.0), 0, 255),
            z: clamp(Math.floor(color_float.z * 255.0), 0, 255)
        };
    }
    else {
        return {
            x: clamp(Math.round(color_float.x * 255.0), 0, 255),
            y: clamp(Math.round(color_float.y * 255.0), 0, 255),
            z: clamp(Math.round(color_float.z * 255.0), 0, 255)
        };
    }
}
// Compressed the input float color to normalized U8 (0 - 1 with 256 steps accuracy)
function compress_rgba8_unorm(color_float) {
    const rgba8 = compress_rgba8ui(color_float);
    return saturate({
        x: rgba8.x / 255.0,
        y: rgba8.y / 255.0,
        z: rgba8.z / 255.0
    });
}
// Utilities for tonemapper
const rrt_and_odt_fit_params = new Float32Array([
    0.0245786, //     k = 0
    -0.000090537, //  m = 1
    0.983729, //      n = 2
    0.4329510, //     p = 3
    0.238081 //      q = 4
]);
// A function used in our actual tonemapper
function rrt_and_odt_fit(v) {
    v = double_truncater(v);
    const a = double_truncater(double_truncater(v * double_truncater(v + rrt_and_odt_fit_params[0])) + rrt_and_odt_fit_params[1]);
    const b = double_truncater(double_truncater(v * double_truncater(double_truncater(rrt_and_odt_fit_params[2] * v) + rrt_and_odt_fit_params[3])) + rrt_and_odt_fit_params[4]);
    return double_truncater(a / b);
}
// A fitted inverse of the function above.
function fitted_inverse_rrt_and_odt_fit(x) {
    if (x < 0.16436) {
        const k = 6.8;
        const m = 0.33;
        const t = 1.48;
        const n = 0.00038;
        const ath = Math.atanh(clamp((x + n) / k, -0.999, 0.999));
        return 1.0 / m * Math.pow(Math.max(0.0, ath), 1.0 / t);
    }
    else {
        const a = 1.0 / (0.49 * 3.14159265356);
        const b = 1.52;
        const c = 0.1152;
        return (Math.tan(Math.min(Math.PI * 0.49, x / a)) + c) / b;
    }
}
// A fitted inverse of our tonemapper, which is not accurate enough to be used for colors on its own.
function fitted_inverse_tonemapper(linear_r, linear_g, linear_b) {
    // Inverse "ACES output mat"
    const odt_sat_r = linear_r * 0.643038 + linear_g * 0.311187 + linear_b * 0.0457755;
    const odt_sat_g = linear_r * 0.0592687 + linear_g * 0.931436 + linear_b * 0.00929492;
    const odt_sat_b = linear_r * 0.0059619 + linear_g * 0.063929 + linear_b * 0.930118;
    const tmp_r = fitted_inverse_rrt_and_odt_fit(odt_sat_r);
    const tmp_g = fitted_inverse_rrt_and_odt_fit(odt_sat_g);
    const tmp_b = fitted_inverse_rrt_and_odt_fit(odt_sat_b);
    // Inverse "ACES input mat"
    const srgb_r = tmp_r * 1.76474 + tmp_g * -0.675778 + tmp_b * -0.0889633;
    const srgb_g = tmp_r * -0.147028 + tmp_g * 1.16025 + tmp_b * -0.0132237;
    const srgb_b = tmp_r * -0.0363368 + tmp_g * -0.162436 + tmp_b * 1.19877;
    return {
        x: double_truncater(srgb_r),
        y: double_truncater(srgb_g),
        z: double_truncater(srgb_b)
    };
}
/**
 * A function which performs our tonemapper for a given linear HDR color value, on CPU.
 * @param linear_r The red channel (linear, HDR)
 * @param linear_g The green channel (linear, HDR)
 * @param linear_b The blue channel (linear, HDR)
 * @returns The tonemapped color in linear space
 */
export function tonemapper(linear_r, linear_g, linear_b) {
    linear_r = double_truncater(linear_r);
    linear_g = double_truncater(linear_g);
    linear_b = double_truncater(linear_b);
    const aces_input = new Float32Array([
        0.59719, 0.07600, 0.02840,
        0.35458, 0.90834, 0.13383,
        0.04823, 0.01566, 0.83777
    ]);
    const rrt_sat_r = double_truncater(double_truncater(double_truncater(linear_r * aces_input[0]) + double_truncater(linear_g * aces_input[3])) +
        double_truncater(linear_b * aces_input[6]));
    const rrt_sat_g = double_truncater(double_truncater(double_truncater(linear_r * aces_input[1]) + double_truncater(linear_g * aces_input[4])) +
        double_truncater(linear_b * aces_input[7]));
    const rrt_sat_b = double_truncater(double_truncater(double_truncater(linear_r * aces_input[2]) + double_truncater(linear_g * aces_input[5])) +
        double_truncater(linear_b * aces_input[8]));
    const fit_r = rrt_and_odt_fit(rrt_sat_r);
    const fit_g = rrt_and_odt_fit(rrt_sat_g);
    const fit_b = rrt_and_odt_fit(rrt_sat_b);
    const aces_output = new Float32Array([
        1.60475, -0.10208, -0.00327,
        -0.53108, 1.10813, -0.07276,
        -0.07367, -0.00605, 1.07602
    ]);
    const srgb_r = double_truncater(double_truncater(double_truncater(fit_r * aces_output[0]) + double_truncater(fit_g * aces_output[3])) +
        double_truncater(fit_b * aces_output[6]));
    const srgb_g = double_truncater(double_truncater(double_truncater(fit_r * aces_output[1]) + double_truncater(fit_g * aces_output[4])) +
        double_truncater(fit_b * aces_output[7]));
    const srgb_b = double_truncater(double_truncater(double_truncater(fit_r * aces_output[2]) + double_truncater(fit_g * aces_output[5])) +
        double_truncater(fit_b * aces_output[8]));
    return {
        x: Math.max(0.0, srgb_r),
        y: Math.max(0.0, srgb_g),
        z: Math.max(0.0, srgb_b)
    };
}
/**
 * This function emulates the reverse process of HDR rendering in the Viewer,
 * i.e. the steps of rendering to the HDR (low-precision) framebuffer -> tonemap -> gamma conversion -> output to RGB8 framebuffer.
 * This is useful for things like background color, which is set in HDR space, but we want a certain RGB8 color in the output (to match things like browser UI).
 * So then you can take the inverse of the background color with this function, and apply that to the HDR framebuffer, and then get back the same, or almost the same, color to your canvas.
 * NOTE: This function does not take camera exposure into account, so that needs to be taken into consideration if applied.
 *
 * This operation is not super-precise, but as close as we can get with our limited bit-depth in our framebuffer (R11F_G11F_B10F).
 * We also limit this operation for reproducing RGB8 colors, i.e. not higher precision inputs.
 * For R,G,B in the range 0-255, you can expect the majority of colors to match, but certain colors will mismatch by at most 1/255, mostly in the blue channel due to its lower precision.
 *
 * This function assumes a few things about the Viewer's framebuffer stack:
 *  - We use the simple gamma->linear conversion when tonemapping (happens in the accumulator), meaning that USE_EXACT_SRGB_CONVERSIONS is NOT defined in Babylon for that shader.
 *  - We use a R11G11B10F (R11F_G11F_B10F) framebuffer (the low precision of this format requires us to take it into account when reversing)
 * @param r The red color channel (range: 0 to 1), in sRGB space
 * @param g The green color channel (range: 0 to 1), in sRGB space
 * @param b The blue color channel (range: 0 to 1), in sRGB space
 * @returns The linear HDR color which will resolve to approximately the input colors after the Viewer's HDR rendering pipeline.
 */
export function get_fitted_inverse_tonemap(framebuffer_type, r, g, b, enable_verbose_warnings) {
    const linear_r = Math.pow(r, GAMMA);
    const linear_g = Math.pow(g, GAMMA);
    const linear_b = Math.pow(b, GAMMA);
    // Start off with a guess from the fitted inverse tonemapper
    const guess = fitted_inverse_tonemapper(linear_r, linear_g, linear_b);
    const goal = compress_rgba8_unorm({ x: r, y: g, z: b });
    // Perform gradient descent until we find our goal in sRGB8
    // step size and iteration count were found by trial and error
    const step = 0.2;
    const iteration_count = 16384;
    for (let i = 0; i < iteration_count; i++) {
        guess.x = Math.min(guess.x, 65000.0);
        guess.y = Math.min(guess.y, 65000.0);
        guess.z = Math.min(guess.z, 65000.0);
        const tonemapped = tonemapper(guess.x, guess.y, guess.z);
        const srgba_compressed = compress_rgba8_unorm(to_srgb_and_clamp(tonemapper(guess.x, guess.y, guess.z)));
        if (srgba_compressed.x === goal.x && srgba_compressed.y === goal.y && srgba_compressed.z === goal.z) {
            // Early out if we've met our goal
            break;
        }
        // Calculate derivative dt/dx where t = tonemapper
        const dx = 0.01;
        const dt_dx = {
            x: (tonemapper(guess.x + dx, guess.y, guess.z).x - tonemapped.x) / dx,
            y: (tonemapper(guess.x, guess.y + dx, guess.z).y - tonemapped.y) / dx,
            z: (tonemapper(guess.x, guess.y, guess.z + dx).z - tonemapped.z) / dx
        };
        guess.x += step * (linear_r - tonemapped.x) * dt_dx.x;
        guess.y += step * (linear_g - tonemapped.y) * dt_dx.y;
        guess.z += step * (linear_b - tonemapped.z) * dt_dx.z;
    }
    if (framebuffer_type === ViewerFramebufferType.R11_G11_B10F) {
        const compressed_guess = compress_r11g11b10f(guess);
        /**
         * For the case or R11_G11_B10F, we need to take special considerations.
         * First of all, this section of code works, but needs some more tweaking when time allows.
         * Second, some quirks about R11_G11_B10F and our tonemapper needs to be established:
         *
         *    * R11_G11_B10F is a great format, but has precision issues with a very small mantissa.
         *      This means after doing reverse tonemapping, we might not actually be able to represent that value in R11_G11_B10F,
         *      meaning the resulting color might be a few bits off for that reason. But it can be much more off, covered in later points.
         *
         *    * We can't easily tell on the CPU side (this side) if we can represent a color fully accurately or not. We can however tell if we are
         *      more than 1-2 bits off. The reason we can't tell precisely is because two precision demotion operations we use on the GPU can either
         *      be done with rounding or truncating, decided by the physical device or driver.
         *       1. For float32 => float11 and float32 => float10, see section 2.1.3 and 2.1.4 (respectively) in the OpenGL ES 3.0.6 specification.
         *       2. For float? => normalized fixed-point (such as with RGBA8), see section 2.1.6.2 in the OpenGL ES 3.0.6 specification.
         *      Both these sections describe that the implementation may decide to round or truncate for both conversions.
         *      Thus, we can't tell with full precision in RGBA8 if the inverse tonemapped color we calculate will produce the exact value requested.
         *      For testing purposes, at the top of this file you will find FLOAT_DOWNCAST_TRUNCATE and FLOAT_NORMALIZATION_TRUNCATE
         *      where you can choose to use rounding or truncating mode for float downcasts and float to normalized fixed point conversion, respectively.
         *
         *    * Our tonemapper is ran in a different color space, meaning that changing the red channel in the input affects red, green and blue output.
         *      (This is what allows it to lower saturation in hotspots, which is a very nice look).
         *
         *    * This means, in order to achieve certain colors after tonemapping, we might actually need negative input values.
         *      One such example is cyan (0, 255, 255). Since G,B channels are so strong after the inverse tonemapper, they will "spill over"
         *      into the red channel which we want to result in 0, thus we need negative values for R. It would be a similar case for (255, 0, 255), etc.
         *      HOWEVER, 11 and 10-bit floats in the OpenGL standard do not have a sign bit and thus do not support negative values,
         *      making colors such as (0, 255, 255) after inverse tonemapping IMPOSSIBLE to store in our framebuffer.
         *
         * But now, to get the best result with R11_G11_B10F, we emulate our postprocessing pipeline which includes a number of conversion steps (between different framebuffers).
         * We then use that evaluated result to try to nudge the result in R11_G11_B10F space, one bit by one, to get us closer to our wanted result.
         * This step is not fully accurate, and we can never make it fully accurate, as we don't know the device's FLOAT_DOWNCAST_TRUNCATE & FLOAT_NORMALIZATION_TRUNCATE
         * modes on the GPU, so we can't match our own flags to it. But it's better than nothing.
         */
        // Number of steps needs tweaking
        for (let i = 0; i < 256; i++) {
            const compressed_tonemapped = tonemapper(compressed_guess.x, compressed_guess.y, compressed_guess.z);
            const clamped = compress_rgba8_unorm(to_srgb_and_clamp(compressed_tonemapped));
            // If we're equal, we can break.
            // We can check for equality only because 'clamped' and 'guess' are both compressed through compress_rgba8 which rounds.
            if (clamped.x === goal.x && clamped.y === goal.y && clamped.z === goal.z) {
                break;
            }
            // Nudge components, bit by bit, to try to achieve the most accurate representation.
            if (clamped.x < goal.x) {
                compressed_guess.x = convert_f64_f11(compressed_guess.x, 1);
            }
            else if (clamped.x > goal.x) {
                compressed_guess.x = convert_f64_f11(compressed_guess.x, -1);
            }
            if (clamped.y < goal.y) {
                compressed_guess.y = convert_f64_f11(compressed_guess.y, 1);
            }
            else if (clamped.y > goal.y) {
                compressed_guess.y = convert_f64_f11(compressed_guess.y, -1);
            }
            if (clamped.z < goal.z) {
                compressed_guess.z = convert_f64_f10(compressed_guess.z, 1);
            }
            else if (clamped.z > goal.z) {
                compressed_guess.z = convert_f64_f10(compressed_guess.z, -1);
            }
        }
        if (enable_verbose_warnings) {
            /**
             * Due to aforementioned lack of reach for certain colors, we need to warn in case the user will a very incorrect color.
             * To do this, we simulate the pipeline used in Views when rendering, which goes as follows:
             *   1. apply color (what we're calculating) in shader or clear color [R11_G11_B10F] =>
             *   2. TONEMAP and accumulate into RGBA8 (Accumulator) =>
             *   3. post TONEMAPPED result back into R11_G11_B10F (View's framebuffer) for temporary storage until View composition
             *   4. composite view's framebuffer back into RGBA8
             * IMPORTANT NOTE:
             *   - Screenshots actually have another flow as they're not composited: they only go through steps 1 & 2.
             *     And yes, this affects the result. The additional RGBA8 (tonemapped) => R11_G11_B10F => RGBA8 steps for normal View make a difference.
             *  Since the last 2 steps only affect the colors in tonemapped space, the effect will be very small and considered neglible here,
             *  and thus we only simulate step 1 and 2 to verify we get the approximately correct tonemapped color.
             */
            const tonemapped_r11_g11_b10f = tonemapper(compressed_guess.x, compressed_guess.y, compressed_guess.z);
            const tonemapped_srgb8ui = compress_rgba8ui(to_srgb_and_clamp(tonemapped_r11_g11_b10f));
            const goal_srgb8ui = compress_rgba8ui({ x: r, y: g, z: b });
            // If the error of any component is greater than the treshold, we warn the user.
            if (Math.abs(tonemapped_srgb8ui.x - goal_srgb8ui.x) > R11_G11_B10F_COMPONENT_MAX_ERROR ||
                Math.abs(tonemapped_srgb8ui.y - goal_srgb8ui.y) > R11_G11_B10F_COMPONENT_MAX_ERROR ||
                Math.abs(tonemapped_srgb8ui.z - goal_srgb8ui.z) > R11_G11_B10F_COMPONENT_MAX_ERROR) {
                console.warn(`Viewer: The color you requested is not accurately representable with the Viewer's framebuffer configuration.
  Please try another color if perfect accuracy is required. High saturation colors are usually problematic, due to the R11_G11_B10F texture format used.
    Requested color: ${goal_srgb8ui.x}, ${goal_srgb8ui.y}, ${goal_srgb8ui.z} (exact)
    Resulting color: ${tonemapped_srgb8ui.x}, ${tonemapped_srgb8ui.y}, ${tonemapped_srgb8ui.z} (estimation)`);
            }
        }
        return {
            x: compressed_guess.x,
            y: compressed_guess.y,
            z: compressed_guess.z
        };
    }
    else {
        if (framebuffer_type === ViewerFramebufferType.RGBA16F) {
            // Clamp our guess to the maximum value supported by F16
            guess.x = Math.min(guess.x, 65000.0);
            guess.y = Math.min(guess.y, 65000.0);
            guess.z = Math.min(guess.z, 65000.0);
        }
        // For other formats, we consider that we don't need compression compensation
        return {
            x: guess.x,
            y: guess.y,
            z: guess.z
        };
    }
}
//# sourceMappingURL=InverseTonemapper.js.map